// jshint curly:true, maxdepth:5, unused:false

import app from 'app';
import UploadWorker from 'worker-loader?inline=no-fallback!worker/upload';
import { mimeType } from '../utils/file-api';
import { workPool } from '@wochitnpm/helpers';

/**
 *
 * Events triggered on model are:
 * upload.finished.metadata - when assetUUID is received
 * upload.finished.chunks   - when all chunks uploaded
 * error                    - on failure of any step
 * finish                   - when done all requested steps
 *
 *
 * metadata.uploadStatus will be resolved to app.uploadStatus.DONE
 * after all of the following steps are complete (with success)
 *
 * 1. assetUUID received
 * 2. file uploaded
 * 3. firebase reported that transcoding is finished
 * 4. model is populated with fresh values from server
 */
app.namespace.Model.UploadQueueModel = Backbone.Model.extend({
  defaults() {
    return {
      type: app.assetType.UNDEFINED,
    };
  },

  initialize(raw, options) {
    this.options = _.options(options, {
      /**
       * Whether to send asset creation request to retrieve id, or not.
       * If not - assuming asset metadata already contains `assetUUID`, and `_uploadId` attached to that model
       */
      createAsset: true,
      /**
       * autoSelected can be used by consumer of model
       */
      autoSelected: false,
      /**
       * one of the upload asset types
       */
      uploadType: app.uploadType.VISUAL_ASSET,
    });

    this._progressTimer = new app.namespace.Util.Timer(
      this._calculateProgressFn.bind(this),
      app.TIME.SECOND
    );
    this._bpsAverage = new app.namespace.Util.Averageble({ population: false });
    this._fileUploadPromise = new _.ColdPromise();
  },

  setUploadStatus(value) {
    /* TODO: eliminate the need in this root-level property ->*/
    this.set('uploadStatus', value);
    this.set(
      'metadata',
      Object.assign({}, this.get('metadata'), { uploadStatus: value })
    );
  },

  /**
   *
   * @return {Promise|*|PromiseLike<T>|Promise<T>}
   */
  startUpload: function () {
    const rv = new _.ColdPromise();

    this._createAsset()
      .then((res) => {
        return this.options.urlUpload ? Promise.resolve : this._uploadFile(res);
      })
      .then(() => {
        this.trigger('upload.finished.chunks', this);

        return this._probeForTranscribe()
          .then(this._waitForTranscoding.bind(this))
          .then(this._refreshModel.bind(this))
          .catch(rv.reject);
      })
      .then(rv.resolve)
      .catch(rv.reject);

    rv.then(
      () => {
        this.trigger('finish', this, { error: false });
      },
      (res) => {
        this.set('uploadErrorMessage', JSON.stringify(res));
        this.trigger('error', this, { error: res });
      }
    );

    return rv;
  },

  _createAsset: function () {
    const rv = new _.ColdPromise();

    if (_.isEmpty(this.get('title'))) {
      this.set({
        title: this._getTitleFromFilename(this.get('fileName')),
      });
    }

    if (this.options.urlUpload) {
      const postData = {
        fileName: this.get('fileName'),
        privacy: this.get('privacy'),
        orgId: this.get('orgId'),
        ownerUserDbId: this.get('ownerUserDbId'),
        mediaAssetSubType: this.get('mediaAssetSubType'),
        title: this.get('title'),
        keywords: this.get('keywords'),
        attribution: this.get('attribution'),
        attributionMode: this.get('attributionMode'),
        userId: app.rootStore.state.user['employeeId'],
      };

      if (this.get('downloadRequestOptions')) {
        postData.downloadRequestOptions = this.get('downloadRequestOptions');
      }

      app.api.asset.initUrlUpload(postData).then(
        (res) => {
          this.set('metadata', Object.assign({}, this.get('metadata'), res));
          rv.resolve(res);
          this.trigger('upload.finished.metadata', this);
        },
        (res) => {
          this.setUploadStatus(app.uploadStatus.FAILED);
          rv.reject(res);
        }
      );
    } else {
      const PREFERRED_PART_SIZE = 10 * 1024 * 1024; // 10 MB
      const numberOfParts = Math.max(
        1,
        Math.ceil(this.get('blob').size / PREFERRED_PART_SIZE)
      );

      const postData = {
        mimeType: this.get('blob').type,
        fileName: this.get('fileName'),
        uploadType: this.options.uploadType,
        numberOfParts,
      };

      app.api.asset.initFileUploadV3(postData).then(
        (res) => {
          this.set(
            'metadata',
            Object.assign({}, this.get('metadata'), {
              assetUUID: res.assetUUID,
              uploadId: res.uploadId,
            })
          );

          rv.resolve(res);
          this.trigger('upload.finished.metadata', this);
        },
        (res) => {
          this.setUploadStatus(app.uploadStatus.FAILED);
          rv.reject(res);
        }
      );
    }

    return rv;
  },

  _getTitleFromFilename: function (filename) {
    if (_.isEmpty(filename)) {
      return this.defaultFilename;
    } else {
      filename = filename.replace(/(.*)\.[^.]+$/g, '$1'); // remove extension
      return _.capitalize(filename);
    }
  },

  _uploadFile: function (uploadConfig) {
    this._fileUploadPromise = new _.ColdPromise();

    this.progress = {
      total: this.get('blob').size,
      // calculated in worker thread
      current: 0.0001,
      percentage: 0,
      // calculated in main thread
      last: 0,
      bps: 0,
      secondsLeft: 0,
    };

    this.uploadWorker = new UploadWorker();
    this.uploadWorker.addEventListener(
      'error',
      this._uploadWorker_failure.bind(this)
    );
    this.uploadWorker.addEventListener(
      'message',
      this._uploadWorker_message.bind(this)
    );

    const postMessageData = {
      uploadConfig: uploadConfig,
      isNarration: this.options.uploadType === app.uploadType.NARRATION_ASSET,
      apiUrl: app.apiUrl(''),
      assetUUID: this.get('metadata').assetUUID,
      fileName: this.get('fileName'),
      privacy: this.get('privacy'),
      orgId: this.get('orgId'),
      ownerUserDbId: this.get('ownerUserDbId'),
      mediaAssetSubType: this.get('mediaAssetSubType'),
      title: this.get('title'),
      keywords: this.get('keywords'),
      attribution: this.get('attribution'),
      attributionMode: this.get('attributionMode'),
      MAX_RETRIES: 4,
      RETRIES_TIMEOUT: app.TIME.second(5),
      channelId: app.rootStore.state.user['channelId'],
      organizationId: localStorage.getItem('selectedOrganizationId') || null,
      authorizationHeader: sessionStorage.getItem('Authorization') || null,
      caption: this.get('metadata').caption,
      transcript: this.get('metadata').transcript,
      shouldNormalizeVideo: !!this.get('_shouldNormalizeVideo'),
    };

    if (this.options.storyId) {
      postMessageData.storyId = this.options.storyId;
    }

    this.uploadWorker.postMessage(JSON.stringify(postMessageData));

    this.setUploadStatus(app.uploadStatus.UPLOAD);

    return this._fileUploadPromise;
  },

  _uploadWorker_message: function (e) {
    const message = JSON.parse(e.data);

    if ('init' === message.type) {
      // after init done - start sending file
      this.uploadWorker.postMessage(this.get('blob'));
      this._progressTimer.trigger();
    } else if ('done' === message.type) {
      // after file successfully uploaded
      this._uploadWorker_done(message.data);
    } else if ('progress' === message.type) {
      // after each file chunk sent
      const step = JSON.parse(message.data);
      $.extend(this.progress, step);
      this.set('progress', $.extend(true, {}, this.progress));
    } else if ('fail' === message.type) {
      this._uploadWorker_failure(message.data);
    } else if ('log' === message.type) {
      console.info('log:', message.data);
    } else if ('debug' === message.type) {
      console.debug(JSON.parse(message.data));
    }
  },

  _calculateProgressFn: function () {
    this._progressTimer.start();

    if (!this.progress.current) {
      return;
    }

    this.progress.bps = this.progress.current - this.progress.last;
    if (this.progress.bps > 0) {
      this._bpsAverage.set(this.progress.bps);
    }
    this.progress.bpsAvg = this._bpsAverage.mean();
    this.progress.last = this.progress.current;

    const bytesLeft = this.progress.total - this.progress.current;
    if (bytesLeft > 0) {
      this.progress.secondsLeft = bytesLeft / this.progress.bpsAvg;
    } else {
      this.progress.secondsLeft = 0;
    }

    this.set('progress', $.extend(true, {}, this.progress));
  },

  _uploadWorker_failure: function (e) {
    let errorMessage = '';
    if (e instanceof ErrorEvent) {
      app.doctorController.report(
        `uploadWorker_failure: @${e.lineno}^${e.colno} ${e.message}`
      );
      errorMessage = e.message;
    } else if (_.isString(e)) {
      errorMessage = e;
    }

    this.setUploadStatus(app.uploadStatus.FAILED);
    this._finishWorkerUpload();
    this._fileUploadPromise.reject(errorMessage);
  },

  _uploadWorker_done: function (response) {
    console.info('_uploadWorker_done', response);
    this._finishWorkerUpload();
    this.setUploadStatus(app.uploadStatus.PROCESSING);
    this._fileUploadPromise.resolve();
  },

  _finishWorkerUpload: function () {
    this._progressTimer.stop();
    this.set('blob', null);
    this.unset('blob');
    this.unset('fileName');

    if (this.uploadWorker) {
      // invoke in delay cause otherwise you can't see last XHR status-code in Chrome-DevTools
      _.delay(() => {
        try {
          this.uploadWorker.terminate();
          this.uploadWorker = null;
          delete this.uploadWorker;
        } catch (ignore) {
          // catch
        }
      }, 128);
    }
  },

  cancelUpload: function () {
    const metadata = this.get('metadata') || {};
    const { assetUUID, uploadId } = metadata;
    const fileName = this.get('fileName');

    this.setUploadStatus(app.uploadStatus.FAILED);
    this._finishWorkerUpload();
    this._fileUploadPromise.reject('Upload cancelled');

    if (uploadId && assetUUID) {
      app.api.asset
        .abortMultipartUpload(assetUUID, uploadId, fileName)
        .catch(() => console.warn('Failed to abort multipart upload'));
    }
  },

  _probeForTranscribe: function () {
    return new Promise((resolve) => {
      const autoTranscribe = this.get('autoTranscribe');
      const isVideo = mimeType.isVideo(this.get('mimeType'));
      const assetUUID = this.get('metadata').assetUUID;
      const fileSize = this.progress && this.progress.total;

      if (isVideo && autoTranscribe) {
        app.rootStore.dispatch('FETCH_TRANSCRIPTION', {
          asset: { metadata: { assetUUID, fileSize } },
          transcribeSubType: app.transcribeSubType.VIDEO,
          how: workPool.job.transcriptions.HOW.CREATE,
          alertOnError: true,
        });
      }

      resolve();
    });
  },

  _waitForTranscoding: function () {
    const rv = new _.ColdPromise();
    const fileSize = this.progress && this.progress.total;
    // variable transcoder timeout depending on the file size
    const timeout = app.TIME.minute(
      fileSize < 1e9 ? 10 : fileSize < 2e9 ? 20 : 30
    );

    const isNarration =
      this.options.uploadType === app.uploadType.NARRATION_ASSET;

    app.api.realTime
      .listenOnAsset(this.get('metadata').assetUUID, { timeout, isNarration })
      .then(rv.resolve, (res) => {
        this.setUploadStatus(app.uploadStatus.FAILED);
        rv.reject(res);
      })
      .finally(() => {
        app.api.realTime.cancelListenOnAsset(this.get('metadata').assetUUID);
      });

    return rv;
  },

  _refreshModel: function () {
    const rv = new _.ColdPromise();
    const isNarration =
      this.options.uploadType === app.uploadType.NARRATION_ASSET;

    app.api.asset
      .fetch(this.get('metadata').assetUUID, isNarration, this.options.storyId)
      .then(
        (res) => {
          // clear existing props (without changing containing object)
          _.each(Object.keys(this.attributes), (key) => {
            if (key !== 'metadata') {
              delete this.attributes[key];
            }
          });

          const asset = isNarration ? res.asset : res;
          // update props
          this.set(asset);
          if (isNarration) {
            this.set('_narrationData', {
              cdnUrl: res.cdnUrl,
              url: res.url,
            });
          }

          // assert: at this moment uploadStatus should be DONE
          if (asset.metadata.uploadStatus !== app.uploadStatus.DONE) {
            // something went wrong...
            const msg =
              'UploadQueueModel::_refreshModel->assertion uploadStatus !== DONE';
            app.doctorController.report(msg);
            rv.reject(msg);
          }

          app.namespace.Controller.AssetController.parse(this.attributes);

          this.setUploadStatus(app.uploadStatus.DONE);
          rv.resolve();
        },
        (res) => {
          this.setUploadStatus(app.uploadStatus.FAILED);
          rv.reject(res);
        }
      );

    return rv;
  },
});

export default app.namespace.Model.UploadQueueModel;
